import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MW_NOWY_HANDLING_COLUMNS,
  buildCompanyDict,
  makeEmptyRow,
  validateCell,
  validateRows,
} from "./schema";
import CompanyCell from "./CompanyCell";

// Tabela "ala Excel" dla trybu "wiele wpisow". Uzytkownik wpisuje dane
// wprost w komorki; walidacja pojawia sie dopiero po dotknieciu komorki
// (blur) - swiezo wygenerowana tabela nie pokazuje bledow.
//
// props:
//   rowCount           - ile wierszy (liczba podana przez uzytkownika)
//   companies          - string[] nazw firm ze slownika Magazyn Warszawa
//   value              - row[] (kontrolowane przez rodzica)
//   onChange(rows)     - nowy stan wierszy
//   onValidityChange({ valid, errorCount })
//   onAddCompany(name) => Promise<string>  - dodaje firme do bazy

const baseCell = {
  border: "1px solid #ccc",
  borderRadius: 4,
  padding: "10px 12px",
  font: "inherit",
  fontSize: 16,
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
};

const resizeRows = (rows, n) => {
  if (rows.length === n) return rows;
  if (rows.length > n) return rows.slice(0, n);
  return [
    ...rows,
    ...Array.from({ length: n - rows.length }, () => makeEmptyRow()),
  ];
};

const isRowComplete = (row) =>
  MW_NOWY_HANDLING_COLUMNS.every(
    (col) => String(row[col.key] ?? "").trim() !== "",
  );

const MwNowyHandlingTable = ({
  rowCount,
  companies = [],
  value = [],
  onChange,
  onValidityChange,
  onAddCompany,
  revealErrorsSignal = 0,
}) => {
  const n = Math.max(0, Math.floor(Number(rowCount) || 0));
  const containerRef = useRef(null);
  const [touched, setTouched] = useState(() => new Set());

  const companyDict = useMemo(() => buildCompanyDict(companies), [companies]);

  // Synchronizacja liczby wierszy z wartoscia podana przez uzytkownika.
  useEffect(() => {
    if (value.length !== n) onChange(resizeRows(value, n));
  }, [n, value, onChange]);

  // Usun z "touched" komorki wierszy, ktore juz nie istnieja.
  useEffect(() => {
    setTouched((prev) => {
      const next = new Set(
        [...prev].filter((key) => Number(key.split(":")[0]) < n),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [n]);

  const { errorsByCell, errorCount } = useMemo(
    () => validateRows(value, { companyDict }),
    [value, companyDict],
  );

  useEffect(() => {
    if (typeof onValidityChange === "function") {
      onValidityChange({
        valid: errorCount === 0 && value.length === n,
        errorCount,
      });
    }
  }, [errorCount, value.length, n, onValidityChange]);

  const markTouched = (rowIdx, colKey) => {
    const key = `${rowIdx}:${colKey}`;
    setTouched((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const setCell = (rowIdx, colKey, cellValue) => {
    const next = value.map((row, i) =>
      i === rowIdx ? { ...row, [colKey]: cellValue } : row,
    );
    onChange(next);
  };

  const handleBlur = (rowIdx, col) => {
    markTouched(rowIdx, col.key);
    // Auto-snap nazwy firmy do pisowni z bazy po opuszczeniu komorki.
    if (col.type === "company") {
      const res = validateCell(col, value[rowIdx]?.[col.key], { companyDict });
      if (res && res.silent && res.fix != null) setCell(rowIdx, col.key, res.fix);
    }
  };

  const focusCell = (rowIdx, colKey) => {
    const el = containerRef.current?.querySelector(
      `[data-cell="${rowIdx}:${colKey}"]`,
    );
    if (el) {
      el.focus();
      if (typeof el.select === "function") el.select();
    }
  };

  const handleKeyDown = (e, rowIdx, colKey) => {
    if (e.key === "Enter") {
      e.preventDefault();
      markTouched(rowIdx, colKey);
      focusCell(rowIdx + 1, colKey);
    }
  };

  const revealAll = () => {
    const all = new Set();
    value.forEach((_, rowIdx) => {
      MW_NOWY_HANDLING_COLUMNS.forEach((col) =>
        all.add(`${rowIdx}:${col.key}`),
      );
    });
    setTouched(all);
  };

  // Po wczytaniu danych z Excela rodzic zwieksza revealErrorsSignal - wtedy
  // od razu pokazujemy walidacje dla wszystkich komorek (bez czekania na blur).
  useEffect(() => {
    if (revealErrorsSignal > 0) revealAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealErrorsSignal]);

  const isCellVisibleError = (rowIdx, colKey) =>
    touched.has(`${rowIdx}:${colKey}`) &&
    Boolean(errorsByCell[`${rowIdx}:${colKey}`]);

  const renderInput = (row, rowIdx, col) => {
    const showErr = isCellVisibleError(rowIdx, col.key);
    const err = errorsByCell[`${rowIdx}:${col.key}`];
    const style = {
      ...baseCell,
      borderColor: showErr ? "#d32f2f" : "#ccc",
      background: showErr ? "#fff5f5" : "#fff",
    };
    const common = {
      "data-cell": `${rowIdx}:${col.key}`,
      value: row[col.key] ?? "",
      style,
      title: showErr ? err.message : undefined,
      onChange: (e) => setCell(rowIdx, col.key, e.target.value),
      onKeyDown: (e) => handleKeyDown(e, rowIdx, col.key),
      onBlur: () => handleBlur(rowIdx, col),
    };

    if (col.type === "enum" && col.widget === "radio") {
      const groupName = `mwnh-${rowIdx}-${col.key}`;
      return (
        <div
          title={showErr ? err.message : undefined}
          style={{
            display: "flex",
            gap: 14,
            alignItems: "center",
            border: `1px solid ${showErr ? "#d32f2f" : "transparent"}`,
            background: showErr ? "#fff5f5" : "transparent",
            borderRadius: 4,
            padding: "5px 6px",
          }}
        >
          {col.options.map((opt, oi) => (
            <label
              key={opt}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <input
                type="radio"
                name={groupName}
                value={opt}
                checked={(row[col.key] ?? "") === opt}
                data-cell={oi === 0 ? `${rowIdx}:${col.key}` : undefined}
                onChange={() => {
                  setCell(rowIdx, col.key, opt);
                  markTouched(rowIdx, col.key);
                }}
                onKeyDown={(e) => handleKeyDown(e, rowIdx, col.key)}
                onBlur={() => handleBlur(rowIdx, col)}
                style={{ margin: 0 }}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }

    if (col.type === "enum") {
      return (
        <select {...common}>
          <option value="">—</option>
          {col.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (col.type === "time") {
      return <input type="time" {...common} />;
    }

    if (col.type === "int") {
      return <input type="number" min="0" step="1" {...common} />;
    }

    if (col.type === "company") {
      return (
        <CompanyCell
          value={row[col.key] ?? ""}
          dataCell={`${rowIdx}:${col.key}`}
          companies={companies}
          dict={companyDict}
          hasError={showErr}
          errorMessage={showErr ? err.message : undefined}
          onChange={(v) => setCell(rowIdx, col.key, v)}
          onCommit={(v) => {
            setCell(rowIdx, col.key, v);
            markTouched(rowIdx, col.key);
          }}
          onBlurCommit={() => handleBlur(rowIdx, col)}
          onKeyDown={(e) => handleKeyDown(e, rowIdx, col.key)}
          onAddCompany={onAddCompany}
        />
      );
    }

    // text (w tym Nr listu z dokladna dlugoscia)
    return (
      <input type="text" maxLength={col.exactLength || undefined} {...common} />
    );
  };

  if (n < 1) return null;

  const visibleErrorList = Object.entries(errorsByCell)
    .filter(([cellKey]) => touched.has(cellKey))
    .map(([cellKey, err]) => {
      const [rowIdx, colKey] = cellKey.split(":");
      const col = MW_NOWY_HANDLING_COLUMNS.find((c) => c.key === colKey);
      return { rowIdx: Number(rowIdx), colKey, col, err };
    })
    .sort((a, b) => a.rowIdx - b.rowIdx);

  const completeRows = value.filter(isRowComplete).length;

  let statusText;
  let statusColor;
  if (visibleErrorList.length > 0) {
    statusColor = "#d32f2f";
    statusText = `Do poprawy: ${visibleErrorList.length} ${
      visibleErrorList.length === 1 ? "komórka" : "komórki/komórek"
    }`;
  } else if (errorCount === 0 && value.length === n) {
    statusColor = "#2e7d32";
    statusText = `Wszystkie ${value.length} wierszy wypełnione poprawnie`;
  } else {
    statusColor = "#666";
    statusText = `Uzupełnij dane w tabeli (${completeRows}/${value.length} wierszy kompletnych)`;
  }

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 500, color: statusColor }}>
          {statusText}
        </span>
        {errorCount > 0 && (
          <button
            type="button"
            onClick={revealAll}
            style={{
              border: "1px solid #ccc",
              borderRadius: 4,
              background: "#fff",
              padding: "3px 10px",
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            Pokaż wszystkie braki
          </button>
        )}
      </div>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 4,
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: 700,
            fontSize: 16,
          }}
        >
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th
                style={{
                  padding: "8px 6px",
                  position: "sticky",
                  left: 0,
                  background: "#f5f5f5",
                  zIndex: 1,
                }}
              >
                #
              </th>
              {MW_NOWY_HANDLING_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: "10px 12px",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    fontSize: 15,
                    minWidth:
                      col.type === "time"
                        ? 135
                        : col.key === "nazwaFirmy"
                          ? 290
                          : 165,
                  }}
                >
                  {col.label} <span style={{ color: "#d32f2f" }}>*</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {value.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ borderTop: "1px solid #eee" }}>
                <td
                  style={{
                    padding: "6px 10px",
                    textAlign: "center",
                    color: "#888",
                    fontSize: 15,
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                  }}
                >
                  {rowIdx + 1}
                </td>
                {MW_NOWY_HANDLING_COLUMNS.map((col) => (
                  <td key={col.key} style={{ padding: "5px 6px" }}>
                    {renderInput(row, rowIdx, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleErrorList.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "10px 0 0",
            fontSize: 14,
          }}
        >
          {visibleErrorList.map(({ rowIdx, colKey, col, err }) => (
            <li
              key={`${rowIdx}:${colKey}`}
              style={{
                padding: "4px 0",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => focusCell(rowIdx, colKey)}
                style={{
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  background: "#fff",
                  padding: "3px 8px",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Wiersz {rowIdx + 1} · {col ? col.label : colKey}
              </button>
              <span style={{ color: "#d32f2f" }}>{err.message}</span>
              {err.suggestion && (
                <button
                  type="button"
                  onClick={() => {
                    setCell(rowIdx, colKey, err.suggestion);
                    focusCell(rowIdx, colKey);
                  }}
                  style={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                    borderRadius: 4,
                    background: "#fff",
                    padding: "3px 8px",
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  Popraw na „{err.suggestion}”
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MwNowyHandlingTable;
